import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ReminderForm from './ReminderForm';
import remindersService from '../../api/services/reminders.service';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate, fmtTime } from '../../utils/formatters';
import { IconTrash } from '../../components/icons';
import { PATHS } from '../../routes/navConfig';

const PAGE_SIZE = 100;

/* "Sep 16, 2026 · 12:12PM" — either half may be missing, both are optional. */
const whenLabel = (r) =>
  [r?.date ? fmtDate(r.date) : null, r?.time ? fmtTime(r.time) : null].filter(Boolean).join(' · ');

/* The row's second line: when it is, and by when parents need to act. */
const metaLabel = (r) =>
  [whenLabel(r), r?.deadline ? `Due by ${fmtDate(r.deadline)}` : null].filter(Boolean).join(' · ') ||
  'No date — just a heads-up';

/* the link a reminder points to, as the row shows it */
const ReminderLink = ({ href }) => (
  <div className="meta">
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{ color: 'var(--green)', fontWeight: 600, textDecoration: 'none', overflowWrap: 'anywhere' }}
    >
      {href}
    </a>
  </div>
);

/* Short class notes with no link attached — its own module, reached from the
   Links hub the same way Sign-Ups is. Arriving with state.openForm (the hub's
   "Add Reminder" tile) lands here with the form already open. */
export default function RemindersPage() {
  const ctx = useContextStore(selectCurrentContext);
  const location = useLocation();
  const navigate = useNavigate();

  const networkId = ctx?.schoolId ? ctx.id : null;

  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [formOpen, setFormOpen] = useState(() => !!location.state?.openForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!networkId) {
      setReminders([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    remindersService
      .list(networkId, { page: 1, limit: PAGE_SIZE })
      .then((r) => { if (!cancelled) setReminders(r.reminders); })
      .catch((err) => {
        if (!cancelled) {
          setReminders([]);
          showToast(err.message);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [networkId, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  /* Reminders live under Links — leaving this module lands back there. */
  const goLinks = () => navigate(PATHS.LINKS);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const toggleAddForm = () => {
    if (formOpen && !editing) setFormOpen(false);
    else {
      setEditing(null);
      setFormOpen(true);
    }
  };

  /* POST for a new reminder; PUT with only what changed for an edit — the API
     rejects a patch carrying nothing but the id. A cleared date, time, link
     or deadline is still "changed", and goes up as '' so the stored value is
     removed. */
  const save = async (fields) => {
    if (!networkId) {
      showToast('Select a class in the header first.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const patch = { id: editing.id };
        if (fields.text !== editing.text) patch.text = fields.text;
        if (fields.date !== editing.date) patch.date = fields.date;
        if (fields.time !== editing.time) patch.time = fields.time;
        if (fields.link !== editing.link) patch.link = fields.link;
        if (fields.deadline !== editing.deadline) patch.deadline = fields.deadline;
        if (Object.keys(patch).length === 1) {
          closeForm();
          return;
        }
        await remindersService.update(patch);
        showToast('Reminder updated ✓');
      } else {
        await remindersService.create({ networkId, ...fields });
        showToast('Reminder added ✓');
      }
      closeForm();
      refresh();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const r = deleteTarget;
    if (!r) return;
    setDeleting(true);
    try {
      await remindersService.remove(networkId, r.id);
      setDeleteTarget(null);
      showToast('Reminder deleted ✓');
      refresh();
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* "Send to parents" hands off to Compose Email with this one reminder in the
     draft — its deadline and link included; `include` must be an object even
     when empty. */
  const sendToParents = (r) => {
    const when = whenLabel(r);
    const due = r.deadline ? ` Please take care of it by ${fmtDate(r.deadline)}.` : '';
    const link = r.link ? `\n\n${r.link}` : '';
    navigate(PATHS.EMAIL, {
      state: {
        linkDraft: {
          subject: `Quick reminder — ${ctx.name}`,
          body: `Hi everyone!\n\nA quick reminder: ${r.text}${when ? ` (${when})` : ''}.${due}${link}\n\nThank you!`,
          include: {},
        },
      },
    });
  };

  return (
    <div className="sec on">
      <SectionHead
        title="Reminders"
        sub="Short text reminders — add a link or a deadline only when one is needed."
        actions={
          <>
            <button className="btn btn-ghost" onClick={goLinks}>← Back to Links</button>
            <button className="btn btn-primary" onClick={toggleAddForm}>+ Add Reminder</button>
          </>
        }
      />

      <Card>
        <ReminderForm open={formOpen} editing={editing} saving={saving} onClose={closeForm} onSave={save} />
        <div>
          {loading && (
            <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
              <Spinner variant="green" /> Loading reminders…
            </div>
          )}
          {!loading && reminders.length === 0 && <EmptyState>No reminders yet.</EmptyState>}
          {!loading &&
            reminders.map((r) => (
              <div className="signup-card" key={r.id}>
                <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 22 }}>📌</div>
                <div className="info">
                  <div className="t">{r.text}</div>
                  <div className="meta">{metaLabel(r)}</div>
                  {r.link && <ReminderLink href={r.link} />}
                </div>
                <div className="actions">
                  <button className="btn btn-primary btn-sm" onClick={() => sendToParents(r)}>
                    Send to parents
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setEditing(r);
                      setFormOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <div className="ev-trash" title="Delete this reminder" onClick={() => setDeleteTarget(r)}>
                    <IconTrash />
                  </div>
                </div>
              </div>
            ))}
        </div>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Reminder"
        confirmLabel="Delete Reminder"
        busy={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      >
        {deleteTarget && (
          <>
            <div className="signup-card" style={{ marginBottom: 12 }}>
              <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 22 }}>📌</div>
              <div className="info">
                <div className="t">{deleteTarget.text}</div>
                <div className="meta">{metaLabel(deleteTarget)}</div>
                {deleteTarget.link && <ReminderLink href={deleteTarget.link} />}
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
              This removes the reminder for good. Emails you already sent are not affected. This can't be undone.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
